import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
} from "@mui/material";
import api from "../axiosInstance";
import { toast } from "react-toastify";
import useStatesUniversities from "../hooks/useStatesUniversities";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  display: "flex",
  flexDirection: "column",
};

const sameId = (a, b) => a !== "" && a != null && String(a) === String(b);

// Dependent options load asynchronously; keep the stored id in formData but only
// hand it to the Select once its option exists, so it renders instead of warning
// about an out-of-range value while the list is still loading.
const selectValue = (options, value) =>
  options.some((o) => sameId(o.id, value)) ? value : "";

// On edit, the mapping row already carries each saved id with its name. While a
// field still holds that saved id and its options list doesn't contain it yet
// (still loading), offer the saved option so its label shows straight away. Once
// the list arrives the same id matches the real option and the label is unchanged.
const withSavedOption = (options, value, saved) =>
  saved && sameId(saved.id, value) && !options.some((o) => sameId(o.id, value))
    ? [saved, ...options]
    : options;

// Builds the saved option only when the mapping has both the id and its name.
const savedOption = (id, nameKey, name, extra = {}) =>
  id != null && name ? { id, [nameKey]: name, ...extra } : null;

const CourseBookModal = ({ open, onClose, mapping, isNew, onSaved }) => {
  const [formData, setFormData] = useState({
    courseId: "",
    universityId: "",
    regulationId: "",
    bookId: "",
    semesterId: "",
    subjectCategoryId: "",
    coreSubjectId: "",
    groupSubjectId: "",
    price: "",
    params: '{"marks": {"long": 0, "short": 0}}',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Local-only filter, never sent to the backend
  const [stateId, setStateId] = useState("");

  // Dropdown options
  const [courses, setCourses] = useState([]);
  const [regulationYears, setRegulationYears] = useState([]);
  const [books, setBooks] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [coreSubjects, setCoreSubjects] = useState([]);
  const [groupSubjects, setGroupSubjects] = useState([]);
  // All universities with their stateId, used to find a mapping's state on edit
  const [universityStates, setUniversityStates] = useState([]);
  // Price of the mapping's book, fetched on edit when the full book list isn't loaded yet
  const [savedBookPrice, setSavedBookPrice] = useState(null);
  // Once the admin edits Price, show exactly what they typed (even empty)
  const [priceTouched, setPriceTouched] = useState(false);
  const booksRef = useRef(books);
  booksRef.current = books;
  console.log(formData);

  const { states, universities, loadingStates, loadingUniversities } =
    useStatesUniversities(stateId);

  // Fetch dropdown data once. Each list is set as soon as it arrives, so a slow
  // request (e.g. the full book list) doesn't hold back the other dropdowns.
  useEffect(() => {
    const load = (request, setter) =>
      request
        .then((res) => setter(res.data.data))
        .catch((err) => {
          const message =
            err.response?.data?.message ||
            err.message ||
            "Something went wrong while fetching dropdown data";
          setErrors(message);
          console.error("Dropdown fetch error:", err);
        });

    load(api.get("/getCourses", { params: { active: true } }), setCourses);
    load(api.get("/api/v1/books"), setBooks);
    load(api.get("/getSemesters"), setSemesters);
    load(api.get("/getSubjectCategories"), setCategories);
    load(api.get("/mentor-catalog/coreSubjects"), setCoreSubjects);
    load(api.get("/api/universities/getAllUniversities"), setUniversityStates);
  }, []);

  // Fetch regulation years when course and university are selected
  useEffect(() => {
    if (formData.courseId && formData.universityId) {
      api
        .get(
          `/api/regulation-years/course/${formData.courseId}/university/${formData.universityId}`
        )
        .then((res) => setRegulationYears(res.data.data || []))

        .catch((err) => {
          console.error("Failed to fetch regulation years:", err);
          setRegulationYears([]);
        });
    } else {
      setRegulationYears([]);
    }
  }, [formData.courseId, formData.universityId]);

  // Fetch group subjects when a core subject is selected
  useEffect(() => {
    if (formData.coreSubjectId) {
      api
        .get("/mentor-catalog/groupedSubjects", {
          params: { core_subject_id: formData.coreSubjectId },
        })
        .then((res) => setGroupSubjects(res.data.data || []))
        .catch((err) => {
          console.error("Failed to fetch group subjects:", err);
          setGroupSubjects([]);
        });
    } else {
      setGroupSubjects([]);
    }
  }, [formData.coreSubjectId]);

  // Populate form on edit
  useEffect(() => {
    if (mapping) {
      setFormData({
        courseId: mapping.courseId || "",
        universityId: mapping.universityId || "",
        regulationId: mapping.regulationId || "",
        bookId: mapping.bookId || "",
        semesterId: mapping.semesterId || "",
        subjectCategoryId: mapping.subjectCategoryId || "",
        coreSubjectId: mapping.coreSubjectId || "",
        groupSubjectId: mapping.groupSubjectId || "",
        price: mapping.price ?? "",
        params: JSON.stringify(
          mapping.params || { marks: { long: 0, short: 0 } }
        ),
      });
      setStateId("");
    } else {
      setFormData({
        courseId: "",
        universityId: "",
        regulationId: "",
        bookId: "",
        semesterId: "",
        subjectCategoryId: "",
        coreSubjectId: "",
        groupSubjectId: "",
        price: "",
        params: '{"marks": {"long": 0, "short": 0}}',
      });
      setStateId("");
    }
  }, [mapping]);

  // Mappings carry universityId (via course_books.course_id -> courses.university_id)
  // but not its state; take the state from the prefetched university list. Runs
  // again if that list arrives after the mapping, but not when the user changes State.
  useEffect(() => {
    if (!mapping?.universityId) return;
    const university = universityStates.find(
      (u) => u.id === mapping.universityId
    );
    if (university?.stateId) setStateId(university.stateId);
  }, [mapping, universityStates]);

  // Saved options from the mapping row, used until each options list has loaded
  const saved = useMemo(() => {
    if (!mapping) return {};
    const university = universityStates.find((u) =>
      sameId(u.id, mapping.universityId)
    );
    return {
      state: savedOption(university?.stateId, "name", university?.stateName),
      university: savedOption(mapping.universityId, "name", mapping.universityName),
      course: savedOption(mapping.courseId, "courseName", mapping.courseName, {
        universityId: mapping.universityId,
      }),
      regulation: savedOption(mapping.regulationId, "regulationName", mapping.regulationName),
      book: savedOption(mapping.bookId, "title", mapping.bookTitle),
      semester: savedOption(mapping.semesterId, "name", mapping.semesterName),
      category: savedOption(mapping.subjectCategoryId, "name", mapping.subjectCategoryName),
      coreSubject: savedOption(mapping.coreSubjectId, "subjectName", mapping.coreSubjectName),
      groupSubject: savedOption(mapping.groupSubjectId, "groupSubjectTitle", mapping.groupSubjectName),
    };
  }, [mapping, universityStates]);

  // Without a course-specific price the book's own price applies. The full book
  // list is slow, so if it hasn't arrived yet fetch just this book's price.
  useEffect(() => {
    setSavedBookPrice(null);
    setPriceTouched(false);
    if (!mapping || mapping.price != null || !mapping.bookId) return;
    if (booksRef.current.some((b) => sameId(b.id, mapping.bookId))) return;
    let cancelled = false;
    api
      .get(`/api/v1/bookById/${mapping.bookId}`)
      .then((res) => {
        if (!cancelled) {
          setSavedBookPrice({ bookId: mapping.bookId, price: res.data.data?.price });
        }
      })
      .catch((err) => console.error("Failed to fetch book price:", err));
    return () => {
      cancelled = true;
    };
  }, [mapping]);

  // State is a local filter only — changing it resets the fields that depend on university
  const handleStateChange = (e) => {
    setStateId(e.target.value);
    setFormData((prev) => ({
      ...prev,
      universityId: "",
      courseId: "",
      regulationId: "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "price") setPriceTouched(true);
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormData((prev) => {
      // Reset courseId when university changes
      if (name === "universityId") {
        return { ...prev, universityId: value, courseId: "" };
      }
      return { ...prev, [name]: value };
    });

    // Reset regulationId when course or university changes
    if (name === "courseId" || name === "universityId") {
      setFormData((prev) => ({ ...prev, regulationId: "" }));
    }

    // Reset groupSubjectId when core subject changes
    if (name === "coreSubjectId") {
      setFormData((prev) => ({ ...prev, groupSubjectId: "" }));
    }
  };
  // Filter courses based on selected university
  const filteredCourses = courses.filter(
    (course) => course.universityId === parseInt(formData.universityId)
  );
  console.log(filteredCourses);

  const stateOptions = withSavedOption(states, stateId, saved.state);
  const universityOptions = withSavedOption(universities, formData.universityId, saved.university);
  const courseOptions = withSavedOption(filteredCourses, formData.courseId, saved.course);
  const regulationOptions = withSavedOption(regulationYears, formData.regulationId, saved.regulation);
  const bookOptions = withSavedOption(books, formData.bookId, saved.book);
  const semesterOptions = withSavedOption(semesters, formData.semesterId, saved.semester);
  const categoryOptions = withSavedOption(categories, formData.subjectCategoryId, saved.category);
  const coreSubjectOptions = withSavedOption(coreSubjects, formData.coreSubjectId, saved.coreSubject);
  const groupSubjectOptions = withSavedOption(groupSubjects, formData.groupSubjectId, saved.groupSubject);

  // Existing pricing: course_books.price, or the book's own price when that is null.
  // formData.price holds only the mapping's own price, so an untouched fallback is
  // saved as null and the stored data stays as it was.
  const bookPrice =
    books.find((b) => sameId(b.id, formData.bookId))?.price ??
    (savedBookPrice && sameId(savedBookPrice.bookId, formData.bookId)
      ? savedBookPrice.price
      : null);
  const displayedPrice =
    priceTouched || formData.price !== "" ? formData.price : bookPrice ?? "";

  const validate = () => {
    const newErrors = {};
    [
      "courseId",
      "universityId",
      "regulationId",
      "bookId",
      "semesterId",
      "subjectCategoryId",
      "coreSubjectId",
      "groupSubjectId",
    ].forEach((field) => {
      if (!formData[field]) newErrors[field] = "Required";
    });

    try {
      JSON.parse(formData.params);
    } catch (e) {
      newErrors.params = "Invalid JSON";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      ...formData,
      courseId: parseInt(formData.courseId),
      universityId: parseInt(formData.universityId),
      regulationId: parseInt(formData.regulationId),
      bookId: parseInt(formData.bookId),
      semesterId: parseInt(formData.semesterId),
      subjectCategoryId: parseInt(formData.subjectCategoryId),
      coreSubjectId: parseInt(formData.coreSubjectId),
      groupSubjectId: parseInt(formData.groupSubjectId),
      price: formData.price === "" ? null : Number(formData.price),
      params: JSON.parse(formData.params),
    };

    setIsLoading(true);
    try {
      if (isNew) {
        await api.post("/api/course-book-mappings", payload);
        toast.success("Course successfully mapped to book.");
      } else {
        const res = await api.put("/api/course-book-mappings", payload);
        toast.success("Course successfully Updated the book mapping");
        // Hand the saved row (as the server returns it) back so the table can update it in place
        onSaved?.(res?.data?.data?.mapping, mapping);
      }

      onClose();
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} disableScrollLock={true}>
      <Box sx={{ ...modalStyle, width: { xs: 340, md: 500 } }}>
        <Typography
          variant="h6"
          sx={{
            px: 4,
            pt: 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          {isNew ? "Add New Mapping" : "Edit Mapping"}
        </Typography>

        <Box sx={{ px: 4, py: 2, overflowY: "auto", flex: 1 }}>
        <FormControl fullWidth margin="normal" size="small">
          <InputLabel>State</InputLabel>
          <Select
            name="stateId"
            value={selectValue(stateOptions, stateId)}
            onChange={handleStateChange}
            label="State"
            disabled={loadingStates}
          >
            {stateOptions.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          fullWidth
          margin="normal"
          size="small"
          error={!!errors.universityId}
        >
          <InputLabel>University</InputLabel>
          <Select
            name="universityId"
            value={selectValue(universityOptions, formData.universityId)}
            onChange={handleChange}
            label="University"
            disabled={!stateId || loadingUniversities}
          >
            {universityOptions.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          fullWidth
          margin="normal"
          size="small"
          error={!!errors.courseId}
        >
          <Autocomplete
            size="small"
            disabled={!formData.universityId}
            options={courseOptions}
            getOptionLabel={(option) => option.courseName}
            value={
              courseOptions.find((c) => sameId(c.id, formData.courseId)) || null
            }
            onChange={(event, newValue) => {
              handleChange({
                target: {
                  name: "courseId",
                  value: newValue ? newValue.id : "",
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Course"
                name="courseId"
                error={!!errors.courseId}
                helperText={errors.courseId}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </FormControl>

        <FormControl
          fullWidth
          margin="normal"
          size="small"
          error={!!errors.regulationId}
        >
          <InputLabel>Regulation Year</InputLabel>
          <Select
            name="regulationId"
            value={selectValue(regulationOptions, formData.regulationId)}
            onChange={handleChange}
            label="Regulation Year"
            disabled={!formData.courseId || !formData.universityId}
          >
            {regulationOptions.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.regulationName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          fullWidth
          margin="normal"
          size="small"
          error={!!errors.bookId}
        >
          <Autocomplete
            options={bookOptions}
            getOptionLabel={(option) =>
              `${option.id} - ${option.title || "Untitled"}`
            }
            value={bookOptions.find((b) => sameId(b.id, formData.bookId)) || null}
            isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
            onChange={(event, newValue) =>
              handleChange({
                target: { name: "bookId", value: newValue ? newValue.id : "" },
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Book"
                variant="outlined"
                size="small"
                error={!!errors.bookId}
                helperText={errors.bookId}
              />
            )}
          />
        </FormControl>

        <FormControl
          fullWidth
          margin="normal"
          size="small"
          error={!!errors.semesterId}
        >
          <InputLabel>Semester</InputLabel>
          <Select
            name="semesterId"
            value={selectValue(semesterOptions, formData.semesterId)}
            onChange={handleChange}
            label="Semester"
          >
            {semesterOptions.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          fullWidth
          margin="normal"
          size="small"
          error={!!errors.subjectCategoryId}
        >
          <InputLabel>Subject Category</InputLabel>
          <Select
            name="subjectCategoryId"
            value={selectValue(categoryOptions, formData.subjectCategoryId)}
            onChange={handleChange}
            label="Subject Category"
          >
            {categoryOptions.map((sc) => (
              <MenuItem key={sc.id} value={sc.id}>
                {sc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          fullWidth
          margin="normal"
          size="small"
          error={!!errors.coreSubjectId}
        >
          <InputLabel>Core Subject</InputLabel>
          <Select
            name="coreSubjectId"
            value={selectValue(coreSubjectOptions, formData.coreSubjectId)}
            onChange={handleChange}
            label="Core Subject"
          >
            {coreSubjectOptions.map((cs) => (
              <MenuItem key={cs.id} value={cs.id}>
                {cs.subjectName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          fullWidth
          margin="normal"
          size="small"
          error={!!errors.groupSubjectId}
        >
          <InputLabel>Group Subject</InputLabel>
          <Select
            name="groupSubjectId"
            value={selectValue(groupSubjectOptions, formData.groupSubjectId)}
            onChange={handleChange}
            label="Group Subject"
            disabled={!formData.coreSubjectId}
          >
            {groupSubjectOptions.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.groupSubjectTitle}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Price (₹)"
          name="price"
          type="number"
          variant="outlined"
          fullWidth
          margin="normal"
          size="small"
          value={displayedPrice}
          onChange={handleChange}
          inputProps={{ min: 0, step: "0.01" }}
        />

        <TextField
          label="Params (JSON)"
          name="params"
          variant="outlined"
          fullWidth
          margin="normal"
          size="small"
          multiline
          rows={3}
          value={formData.params}
          onChange={handleChange}
          error={!!errors.params}
          helperText={
            errors.params || `Format: {"marks":{"long":12,"short":4}}`
          }
        />
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          sx={{
            px: 4,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button onClick={onClose} color="secondary" sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CourseBookModal;
